import React, { useState, useEffect, useMemo } from 'react';
import { Crown, Search, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import { usersAPI, HostUser } from '../../services/usersAPI';
import { useNotification } from '../../hooks/useNotification';
import Modal from '../UI/Modal';
import Input from '../UI/Input';
import Button from '../UI/Button';
import LoadingSpinner from '../UI/LoadingSpinner';
import { logError } from '../../utils/errorLogger';
import clsx from 'clsx';

interface MirsalMemberManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void; // Callback when status is updated
}

const MirsalMemberManagementModal: React.FC<MirsalMemberManagementModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const { showNotification } = useNotification();
  const [hosts, setHosts] = useState<HostUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Show 10 hosts per page

  useEffect(() => {
    if (isOpen) {
      loadHosts();
    } else {
      // Reset state when modal closes
      setHosts([]);
      setSearchTerm('');
      setCurrentPage(1);
    }
  }, [isOpen]);

  // Filter hosts based on search term
  const filteredHosts = useMemo(() => {
    if (!searchTerm.trim()) {
      return hosts;
    }
    const search = searchTerm.toLowerCase();
    return hosts.filter(
      (host) =>
        host.get_full_name.toLowerCase().includes(search) ||
        host.email?.toLowerCase().includes(search) ||
        host.phone?.toLowerCase().includes(search) ||
        host.first_name.toLowerCase().includes(search) ||
        host.last_name.toLowerCase().includes(search)
    );
  }, [searchTerm, hosts]);

  // Paginate filtered hosts
  const paginatedHosts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredHosts.slice(startIndex, endIndex);
  }, [filteredHosts, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredHosts.length / itemsPerPage);

  useEffect(() => {
    // Reset to page 1 when search term changes
    setCurrentPage(1);
  }, [searchTerm]);

  const loadHosts = async () => {
    try {
      setLoading(true);
      const hostsData = await usersAPI.getHosts();
      setHosts(hostsData);
    } catch (error: any) {
      logError('Failed to load hosts', error);
      showNotification('Failed to load hosts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMirsalMember = async (host: HostUser) => {
    const newStatus = !host.is_mirsal_member;
    try {
      setUpdating(host.id);
      const response = await usersAPI.updateMirsalMemberStatus(host.id, newStatus);
      
      if (response.success) {
        showNotification(
          `${host.get_full_name} is now ${newStatus ? 'a' : 'not a'} Mirsal member`,
          'success'
        );
        
        // Update local state
        setHosts((prev) =>
          prev.map((h) =>
            h.id === host.id ? { ...h, is_mirsal_member: newStatus } : h
          )
        );
        
        // Trigger parent update if callback provided
        if (onUpdate) {
          onUpdate();
        }
      } else {
        showNotification('Failed to update Mirsal member status', 'error');
      }
    } catch (error: any) {
      logError('Failed to update Mirsal member status', error);
      showNotification(
        error.response?.data?.error?.message || 'Failed to update Mirsal member status',
        'error'
      );
    } finally {
      setUpdating(null);
    }
  };

  const mirsalMembersCount = hosts.filter((h) => h.is_mirsal_member).length;
  const nonMirsalMembersCount = hosts.length - mirsalMembersCount;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Mirsal Members"
      size="lg"
    >
      <div className="space-y-4">
        {/* Summary Stats */}
        <div className={`grid grid-cols-2 gap-4 p-4 rounded-lg border ${
          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="text-center">
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Mirsal Members
            </p>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {mirsalMembersCount}
            </p>
          </div>
          <div className="text-center">
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Regular Hosts
            </p>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {nonMirsalMembersCount}
            </p>
          </div>
        </div>

        {/* Search */}
        <div>
          <Input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>

        {/* Hosts List */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        ) : filteredHosts.length === 0 ? (
          <div className={`text-center py-12 rounded-lg ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
          }`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {searchTerm ? 'No hosts found matching your search' : 'No hosts found'}
            </p>
          </div>
        ) : (
          <>
            <div className={`max-h-[60vh] overflow-y-auto rounded-lg border ${
              theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedHosts.map((host) => (
                <div
                  key={host.id}
                  className={`p-4 hover:bg-opacity-50 transition-colors ${
                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className={`text-base font-semibold truncate ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {host.get_full_name}
                        </h4>
                        {host.is_mirsal_member && (
                          <Crown className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        {host.email && (
                          <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                            {host.email}
                          </span>
                        )}
                        {host.phone && (
                          <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                            {host.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <span
                        className={clsx(
                          'px-3 py-1 rounded-full text-xs font-medium',
                          host.is_mirsal_member
                            ? theme === 'dark'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-yellow-100 text-yellow-800'
                            : theme === 'dark'
                            ? 'bg-gray-700 text-gray-400'
                            : 'bg-gray-100 text-gray-600'
                        )}
                      >
                        {host.is_mirsal_member ? 'Mirsal Member' : 'Regular Host'}
                      </span>
                      {!host.is_mirsal_member && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleToggleMirsalMember(host)}
                          loading={updating === host.id}
                          leftIcon={<Check className="h-4 w-4" />}
                        >
                          Make Member
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`flex items-center justify-between pt-4 border-t ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredHosts.length)} of {filteredHosts.length} hosts
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    leftIcon={<ChevronLeft className="h-4 w-4" />}
                  >
                    Previous
                  </Button>
                  <span className={`px-3 py-1 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    rightIcon={<ChevronRight className="h-4 w-4" />}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MirsalMemberManagementModal;

